import type {
  AreaOrigin,
  ConductorMaterial,
  ConductorRole,
  DisconnectionTimeSource,
  ElectrodeKind,
  EnergyBasis,
  FaultCurrentOrigin,
  KOrigin,
  KTableId,
  SeasonalFactorOrigin,
  SystemKind,
  TargetOrigin,
} from "../services/earthingContract";
import type { ValidationLabels } from "../utils/validationMessages";

// The words of the earthing study (EOS-08a, Master Prompt A36): the form's labels, keyed by
// the request path with list indexes as "*", and the label maps of the controlled vocabularies
// the panels print. Exhaustive maps: a new member without a label is a compile error.

export const SYSTEM_KIND_LABELS: Record<SystemKind, string> = {
  TN_S: "TN-S",
  TN_C_S: "TN-C-S",
  TT: "TT",
  IT: "IT",
};

export const ROLE_LABELS: Record<ConductorRole, string> = {
  CIRCUIT_PROTECTIVE: "Circuit protective conductor",
  MAIN_PROTECTIVE: "Main protective conductor (board)",
  EARTHING_CONDUCTOR: "Earthing conductor (MET to electrode)",
};

export const MATERIAL_LABELS: Record<ConductorMaterial, string> = {
  COPPER: "Copper",
  ALUMINIUM: "Aluminium",
  STEEL: "Steel",
};

export const K_TABLE_LABELS: Record<KTableId, string> = {
  IS3043_11A: "IS 3043 Table 11A - bare, no risk of fire",
  IS3043_11B: "IS 3043 Table 11B - insulated, not in a cable",
  IS3043_11C: "IS 3043 Table 11C - core of a multicore cable",
  IS3043_11D: "IS 3043 Table 11D - bare, hazardous area",
  CPWD_F6: "CPWD App F Table F-6 - bare, no risk to neighbours",
  CPWD_F7: "CPWD App F Table F-7 - bare, on a cable covering",
};

/** The words of every column of every held table (the data files' column ids). */
export const K_COLUMN_LABELS: Record<string, string> = {
  BARE: "bare conductor",
  PVC: "PVC",
  BUTYL_RUBBER: "butyl rubber",
  XLPE_EPR: "XLPE / EPR",
  FINAL_150: "final 150 °C",
  FINAL_200: "final 200 °C",
  VISIBLE_RESTRICTED: "visible and in restricted areas",
  NORMAL: "normal conditions",
  FIRE_RISK: "fire risk",
  THERMOPLASTIC: "thermoplastic covering",
  POLYETHYLENE_CSP: "polyethylene (CSP) covering",
};

export const ELECTRODE_KIND_LABELS: Record<ElectrodeKind, string> = {
  ROD_OR_PIPE: "Rod or pipe",
  PLATE: "Plate",
  STRIP: "Strip",
  ROUND_CONDUCTOR: "Round conductor",
};

export const FAULT_CURRENT_ORIGIN_LABELS: Record<FaultCurrentOrigin, string> = {
  LINKED_FAULT_RUN: "Linked earth-fault run",
  LINKED_DISCONNECTION_ROW: "Linked earth-fault loop row (I_f)",
  DECLARED: "Declared current",
  NOT_ESTABLISHED: "Not established",
};

export const DISCONNECTION_TIME_SOURCE_LABELS: Record<DisconnectionTimeSource, string> = {
  LINKED_DISCONNECTION_ROW: "Linked row's required time (maximum permitted)",
  DECLARED: "Declared time",
  NOT_ESTABLISHED: "Not established",
};

export const ENERGY_BASIS_LABELS: Record<EnergyBasis, string> = {
  COMPUTED_FROM_CURRENT_AND_TIME: "I² × t",
  DECLARED: "Declared let-through energy",
  NOT_ESTABLISHED: "Not established",
};

export const K_ORIGIN_LABELS: Record<KOrigin, string> = {
  REGISTER_VERIFIED: "Held table (register, VERIFIED copy)",
  DECLARED: "Declared, not verified",
  NOT_ESTABLISHED: "Not established",
};

export const AREA_ORIGIN_LABELS: Record<AreaOrigin, string> = {
  DECLARED: "Declared area",
  LINKED_DISCONNECTION_ROW: "Linked row's protective conductor",
  NOT_ESTABLISHED: "Not established",
};

export const SEASONAL_FACTOR_ORIGIN_LABELS: Record<SeasonalFactorOrigin, string> = {
  DECLARED: "Measured × declared seasonal factor",
  NOT_DECLARED: "Measured value (no seasonal factor)",
  NOT_APPLICABLE: "No electrodes",
};

export const TARGET_ORIGIN_LABELS: Record<TargetOrigin, string> = {
  DECLARED: "Declared target",
  NOT_ESTABLISHED: "No target declared",
};

export const EARTHING_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.bus_code": "Bus",
    "study.system_kind": "System earthing",
    "study.system_voltage_v": "System voltage",
    "study.system_voltage_v.value": "System voltage (V)",
    "study.system_voltage_v.source": "System voltage source",
    "study.sld_bus_code": "Bus (SLD)",
    "study.conductors": "Protective conductors",
    "study.conductors.*.code": "Conductor code",
    "study.conductors.*.name": "Conductor name",
    "study.conductors.*.role": "Role",
    "study.conductors.*.fault_run_id": "Earth-fault run",
    "study.conductors.*.disconnection_run_id": "Earth-fault loop run",
    "study.conductors.*.disconnection_row_code": "Circuit row",
    "study.conductors.*.declared_fault_current_a": "Declared fault current",
    "study.conductors.*.declared_fault_current_a.value": "Fault current (A)",
    "study.conductors.*.declared_fault_current_a.source": "Fault current source",
    "study.conductors.*.declared_let_through_energy_a2s": "Let-through energy",
    "study.conductors.*.declared_let_through_energy_a2s.value": "Let-through energy I²t (A²s)",
    "study.conductors.*.declared_let_through_energy_a2s.source": "Let-through energy source",
    "study.conductors.*.declared_disconnection_time_s": "Declared disconnection time",
    "study.conductors.*.declared_disconnection_time_s.value": "Disconnection time (s)",
    "study.conductors.*.declared_disconnection_time_s.source": "Disconnection time source",
    "study.conductors.*.declared_area_mm2": "Declared conductor area",
    "study.conductors.*.declared_area_mm2.value": "Conductor area (mm²)",
    "study.conductors.*.declared_area_mm2.source": "Conductor area source",
    "study.conductors.*.conductor_material": "Material",
    "study.conductors.*.declared_k": "Declared k",
    "study.conductors.*.declared_k.value": "k (A·√s/mm²)",
    "study.conductors.*.declared_k.source": "k source",
    "study.conductors.*.k_table_case": "k table case",
    "study.conductors.*.k_table_case.table": "k table",
    "study.conductors.*.k_table_case.column": "k table column",
    "study.conductors.*.sld_element_code": "Feeder (SLD)",
    "study.conductors.*.notes": "Conductor notes",
    "study.electrodes": "Earth electrodes",
    "study.electrodes.*.code": "Electrode code",
    "study.electrodes.*.kind": "Electrode kind",
    "study.electrodes.*.rod_length_m": "Rod or pipe length (m)",
    "study.electrodes.*.rod_diameter_mm": "Rod or pipe diameter (mm)",
    "study.electrodes.*.plate_length_m": "Plate length (m)",
    "study.electrodes.*.plate_width_m": "Plate width (m)",
    "study.electrodes.*.strip_length_m": "Strip length (m)",
    "study.electrodes.*.strip_width_mm": "Strip width (mm)",
    "study.electrodes.*.conductor_length_m": "Conductor length (m)",
    "study.electrodes.*.conductor_diameter_mm": "Conductor diameter (mm)",
    "study.electrodes.*.dimension_source": "Dimension source",
    "study.electrodes.*.material": "Electrode material",
    "study.electrodes.*.count_in_parallel": "Number in parallel",
    "study.electrodes.*.parallel_factor": "Utilisation factor",
    "study.electrodes.*.parallel_factor.value": "Utilisation factor",
    "study.electrodes.*.parallel_factor.source": "Utilisation factor source",
    "study.electrodes.*.spacing_m": "Spacing (m)",
    "study.electrodes.*.notes": "Electrode notes",
    "study.soil": "Soil",
    "study.soil.resistivity_ohm_m": "Soil resistivity (Ω·m)",
    "study.soil.test_method": "Test method",
    "study.soil.test_date": "Test date",
    "study.soil.source": "Test report",
    "study.soil.seasonal_factor": "Seasonal factor",
    "study.soil.seasonal_factor.value": "Seasonal factor",
    "study.soil.seasonal_factor.source": "Seasonal factor source",
    "study.target_resistance_ohm": "Target resistance",
    "study.target_resistance_ohm.value": "Target resistance (Ω)",
    "study.target_resistance_ohm.source": "Target resistance source",
    "study.jurisdiction_profile": "Jurisdiction profile",
    "study.notes": "Notes",
    project_revision_id: "Project revision",
    notes: "Run notes",
  },
  groups: {
    conductors: "Conductor",
    electrodes: "Electrode",
  },
};
